import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { apiBase } from '../utils/authHeaders';
import {
  capabilitiesAllow, errorMessage, readMktScope, requestConfig, scopeKey, useMktScope,
} from './MarketplaceShared';

export {
  capabilitiesAllow, errorMessage, requestConfig, scopeKey, useMktScope,
} from './MarketplaceShared';

export const wfApi = axios.create({ baseURL: `${apiBase()}/api/workforce` });
export const readWfScope = readMktScope;
export const useWfScope = useMktScope;

export function useWorkforceNavEnabled(token) {
  const scope = useWfScope(token);
  const key = scopeKey(scope);
  const [allowedKey, setAllowedKey] = useState('');
  useEffect(() => {
    const controller = new AbortController();
    setAllowedKey('');
    if (scope.orgId && scope.token) {
      wfApi.get('/capabilities', requestConfig(scope, controller.signal))
        .then(res => { if (!controller.signal.aborted && capabilitiesAllow(res?.data)) setAllowedKey(key); })
        .catch(() => {});
    }
    return () => controller.abort();
  }, [key, scope]);
  return allowedKey === key;
}

export function useWfRuntime(scope) {
  const lifetime = useRef(new AbortController());
  const lock = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  useEffect(() => {
    const controller = new AbortController();
    lifetime.current = controller;
    return () => controller.abort();
  }, []);
  const active = useCallback(
    () => !lifetime.current.signal.aborted && scopeKey(readWfScope()) === scopeKey(scope),
    [scope],
  );
  const request = useCallback(async (method, path, { body, params } = {}) => {
    const config = { ...requestConfig(scope, lifetime.current.signal) };
    if (params) config.params = params;
    const res = ['get', 'delete'].includes(method)
      ? await wfApi[method](path, config)
      : await wfApi[method](path, body, config);
    return res.data;
  }, [scope]);
  const run = useCallback(async (work, success) => {
    if (lock.current || !active()) return undefined;
    lock.current = true;
    setBusy(true); setError(''); setNotice('');
    try {
      const result = await work();
      if (active() && success) setNotice(success);
      return { ok: true, result };
    } catch (e) {
      if (active()) setError(errorMessage(e));
      return { ok: false, error: e };
    } finally {
      lock.current = false;
      if (active()) setBusy(false);
    }
  }, [active]);
  return { active, request, run, busy, error, notice, setError, setNotice };
}

export const asList = (v, key) => {
  if (Array.isArray(v)) return v;
  if (key && Array.isArray(v?.[key])) return v[key];
  return Array.isArray(v?.items) ? v.items : [];
};

export function Messages({ error, notice }) {
  return (
    <>
      {error && <div className="wf-error" role="alert">{error}</div>}
      {notice && <div className="wf-notice" role="status">{notice}</div>}
    </>
  );
}
